/**
 * Utility functions for working with YouTube URLs
 */

/**
 * Extract YouTube video ID from various YouTube URL formats
 * Supports:
 * - Regular: https://www.youtube.com/watch?v=VIDEO_ID
 * - Shorts: https://www.youtube.com/shorts/VIDEO_ID
 * - Embed: https://www.youtube.com/embed/VIDEO_ID
 * - Shortened: https://youtu.be/VIDEO_ID
 */
export const getYouTubeVideoId = (videoUrl?: string): string | null => {
  if (!videoUrl) return null;
  
  // YouTube Shorts pattern: youtube.com/shorts/VIDEO_ID
  const shortsMatch = videoUrl.match(/youtube\.com\/shorts\/([^"&?\/\s]{11})/);
  if (shortsMatch && shortsMatch[1]) {
    return shortsMatch[1];
  }
  
  // Regular YouTube URL patterns
  const youtubeRegex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/;
  const match = videoUrl.match(youtubeRegex);
  
  if (match && match[1]) {
    return match[1];
  }
  
  return null;
};

/**
 * Get YouTube thumbnail URL from video URL
 */
export const getYouTubeThumbnail = (videoUrl?: string): string | null => {
  const videoId = getYouTubeVideoId(videoUrl);
  if (videoId) {
    return `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`;
  }
  return null;
};

/**
 * Check if URL is a YouTube URL
 */
export const isYouTubeUrl = (videoUrl?: string): boolean => {
  return getYouTubeVideoId(videoUrl) !== null;
};

export type YouTubeEmbedOptions = {
  /**
   * Start playback when iframe loads. Pair with mute (set automatically) so mobile browsers
   * actually start playback on one tap; unmuted autoplay is often blocked after async iframe mount.
   */
  autoplay?: boolean;
};

/**
 * Get YouTube embed URL from video URL.
 * Adds params that encourage inline playback in mobile webviews (playsinline) vs opening the YouTube app/tab.
 */
export const getYouTubeEmbedUrl = (
  videoUrl?: string,
  options?: YouTubeEmbedOptions
): string | null => {
  const videoId = getYouTubeVideoId(videoUrl);
  if (!videoId) {
    return null;
  }
  const params = new URLSearchParams({
    playsinline: '1',
    modestbranding: '1',
    rel: '0',
  });
  if (options?.autoplay) {
    params.set('autoplay', '1');
    params.set('mute', '1');
  }
  if (typeof window !== 'undefined' && window.location?.origin) {
    params.set('origin', window.location.origin);
  }
  return `https://www.youtube.com/embed/${videoId}?${params.toString()}`;
};

